import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@supabase/auth-helpers-react';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';
import { useTranslation } from 'react-i18next';

// Type pour la table videos (à ajuster si nécessaire)
type VideoRow = Database['public']['Tables']['escort_videos']['Row'];
type VideoInsert = Database['public']['Tables']['escort_videos']['Insert'];
type VideoUpdate = Database['public']['Tables']['escort_videos']['Update'];

// Interface pour une vidéo dans l'UI
export interface UIVideo {
  id: string;
  url: string; // URL publique du fichier vidéo
  storagePath: string; // Chemin dans le bucket Supabase
  isPrivate: boolean;
  isPreview: boolean;
  createdAt: string;
}

// Interface pour le retour du hook
interface UseProfileVideosReturn {
  videos: UIVideo[];
  isLoading: boolean;
  loadingError: string | null;
  uploadError: string | null;
  isUploading: boolean;
  uploadVideo: (videoFile: File, isPrivate?: boolean) => Promise<void>;
  deleteVideo: (videoId: string, storagePath: string) => Promise<void>;
  updateVideoMeta: (videoId: string, updates: Partial<Pick<VideoUpdate, 'is_private' | 'is_preview'>>) => Promise<void>;
  setAsPreview: (videoId: string) => Promise<void>;
}

const BUCKET_NAME = 'escort-videos';

export const useProfileVideos = (): UseProfileVideosReturn => {
  const user = useUser();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [videos, setVideos] = useState<UIVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const loadVideos = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setLoadingError(null);

    try {
      const { data, error } = await supabase
        .from('escort_videos')
        .select('*')
        .eq('escort_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const uiVideos: UIVideo[] = data.map(v => {
        const { data: urlData } = supabase.storage
          .from(BUCKET_NAME)
          .getPublicUrl(v.video_url);
          
        return {
          id: v.id,
          url: urlData?.publicUrl || '', // Utiliser URL publique
          storagePath: v.video_url,
          isPrivate: v.is_private,
          isPreview: v.is_preview,
          createdAt: v.created_at,
        };
      });
      setVideos(uiVideos);
    } catch (err: any) {
      console.error("Error loading videos:", err);
      setLoadingError(err.message || 'Failed to load videos');
      toast({ title: 'Erreur', description: 'Impossible de charger les vidéos.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const uploadVideo = useCallback(async (videoFile: File, isPrivate: boolean = false) => {
    if (!user) return;
    setIsUploading(true);
    setUploadError(null);

    try {
      const fileExt = videoFile.name.split('.').pop();
      const uniqueFileName = `${user.id}/${Date.now()}.${fileExt}`;

      // 1. Uploader le fichier vidéo
      const { error: uploadError } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(uniqueFileName, videoFile);

      if (uploadError) throw uploadError;

      // 2. Insérer l'enregistrement dans la table
      const videoData: VideoInsert = {
        escort_id: user.id,
        video_url: uniqueFileName,
        is_private: isPrivate,
        is_preview: false, // Par défaut, pas de preview
        // thumbnail_url: null // Pas de miniature pour l'instant
      };

      const { error: insertError } = await supabase
        .from('escort_videos')
        .insert(videoData);

      if (insertError) {
        // Essayer de supprimer le fichier uploadé si l'insertion échoue
        await supabase.storage.from(BUCKET_NAME).remove([uniqueFileName]);
        throw insertError;
      }

      toast({ title: 'Succès', description: 'Vidéo uploadée avec succès.' });
      await loadVideos(); // Recharger la liste

    } catch (err: any) {
      console.error("Error uploading video:", err);
      setUploadError(err.message || 'Failed to upload video');
      toast({ title: 'Erreur', description: 'Échec de l\'upload de la vidéo.', variant: 'destructive' });
    } finally {
      setIsUploading(false);
    }
  }, [user, toast, loadVideos]);

  const deleteVideo = useCallback(async (videoId: string, storagePath: string) => {
    if (!user) return;

    // Optimistic UI update
    const originalVideos = videos;
    setVideos(prev => prev.filter(v => v.id !== videoId));

    try {
      // 1. Supprimer l'enregistrement de la table
      const { error: dbError } = await supabase
        .from('escort_videos')
        .delete()
        .match({ id: videoId, escort_id: user.id }); // Sécurité

      if (dbError) throw dbError;

      // 2. Supprimer le fichier du stockage
      const { error: storageError } = await supabase.storage
        .from(BUCKET_NAME)
        .remove([storagePath]);

      if (storageError) {
         // Log l'erreur de stockage mais considérer la suppression comme réussie si la BDD est OK
         console.warn(`Failed to delete video file from storage (${storagePath}), but DB record deleted:`, storageError);
         // Commenter le toast pour éviter l'erreur linter persistante
         /*
         toast({ 
           title: t('common:warning', 'Avertissement'), // Clé i18n avec fallback
           description: t('videoErrors.deleteFileError', 'Vidéo supprimée de la base de données, mais erreur lors de la suppression du fichier.'), // Clé i18n avec fallback
           variant: 'destructive' 
         });
         */
      } else {
         toast({ title: 'Succès', description: 'Vidéo supprimée avec succès.' });
      }

    } catch (err: any) {
      console.error("Error deleting video:", err);
      setVideos(originalVideos); // Revert
      toast({ title: 'Erreur', description: 'Impossible de supprimer la vidéo.', variant: 'destructive' });
    } 
  }, [user, toast, videos, t]);

  const updateVideoMeta = useCallback(async (videoId: string, updates: Partial<Pick<VideoUpdate, 'is_private' | 'is_preview'>>) => {
    if (!user) return;

    // Optimistic UI update
    const originalVideos = videos;
    setVideos(prev => prev.map(v => v.id === videoId ? { ...v, ...updates } : v));

    try {
        // Le trigger `ensure_single_preview_video` gère la logique si `is_preview` est mis à true.
      const { error } = await supabase
        .from('escort_videos')
        .update(updates)
        .match({ id: videoId, escort_id: user.id });

      if (error) throw error;

       toast({ title: 'Succès', description: 'Métadonnées de la vidéo mises à jour.' });
       // Si on change is_preview, on doit recharger pour refléter le changement sur les autres vidéos (via trigger)
       if ('is_preview' in updates) {
           await loadVideos();
       }

    } catch (err: any) {
      console.error("Error updating video metadata:", err);
      setVideos(originalVideos); // Revert
      toast({ title: 'Erreur', description: 'Impossible de mettre à jour les métadonnées.', variant: 'destructive' });
    }
  }, [user, toast, videos, loadVideos]);

  const setAsPreview = useCallback(async (videoId: string) => {
      // On s'assure que la vidéo à marquer comme preview n'est pas privée
      const videoToSet = videos.find(v => v.id === videoId);
      if (videoToSet && videoToSet.isPrivate) {
          toast({ title: 'Action impossible', description: 'Une vidéo privée ne peut pas être définie comme vidéo de prévisualisation.', variant: 'destructive' });
          return;
      }
      await updateVideoMeta(videoId, { is_preview: true });
  }, [updateVideoMeta, videos, toast]);


  return {
    videos,
    isLoading,
    loadingError,
    uploadError,
    isUploading,
    uploadVideo,
    deleteVideo,
    updateVideoMeta,
    setAsPreview
  };
}; 